import React from 'react';

import { ValueBox, ValueGroup } from '@candu-ai/craft/ui';
import { useResponsiveCss } from 'craft/hooks';
import { CommonNodeProperties, CssSelectorId } from 'craft/types';
import { createToggle } from 'craft/utils';

import { Gap } from './Gap';
import {
  AlignBaseline,
  AlignCenter,
  AlignFlexEnd,
  AlignFlexStart,
  AlignStretch,
  FlexDirectionColumn,
  FlexDirectionRow,
  FlexNoWrap,
  FlexWrap,
  JustifyCenter,
  JustifyFlexEnd,
  JustifyFlexStart,
  JustifySpaceAround,
  JustifySpaceBetween,
  JustifySpaceEvenly,
} from './Toggles';

interface Props {
  nodeIds: string[];
  cssSelectorId?: CssSelectorId;
}

export const Flex = ({ nodeIds, cssSelectorId }: Props) => {
  const { responsiveCss, setResponsiveCss } = useResponsiveCss(
    nodeIds,
    CommonNodeProperties.Multiple,
    cssSelectorId,
  );
  const { flexDirection = 'row', flexWrap, justifyContent, alignItems } = responsiveCss || {};
  const toggleValues = createToggle(responsiveCss, setResponsiveCss);

  return (
    <>
      <ValueGroup data-cy="toolbox-content-layout">
        <ValueBox
          data-cy="toolbox-flex-direction-valuebox"
          label="Content direction"
          isInGroup={{ isFirst: true }}
        >
          <FlexDirectionRow flexDirection={flexDirection} setCss={setResponsiveCss} />
          <FlexDirectionColumn flexDirection={flexDirection} setCss={setResponsiveCss} />
        </ValueBox>
        <ValueBox
          data-cy="toolbox-flex-wrap-valuebox"
          label="Wrap mode"
          isInGroup={{ isLast: true }}
        >
          <FlexNoWrap setCss={setResponsiveCss} flexWrap={flexWrap || 'no-wrap'} />
          <FlexWrap setCss={setResponsiveCss} flexWrap={flexWrap} />
        </ValueBox>
      </ValueGroup>
      {/* Alignment */}
      <ValueGroup>
        <ValueBox
          id="justify-box"
          data-cy="toolbox-flex-justify-valuebox"
          label={flexDirection === 'row' ? 'Horizontal' : 'Vertical'}
          isInGroup={{ isFirst: true }}
        >
          <JustifyFlexStart
            toggleValues={toggleValues}
            justifyContent={justifyContent || 'flex-start'}
            flexDirection={flexDirection}
          />
          <JustifyCenter
            toggleValues={toggleValues}
            justifyContent={justifyContent}
            flexDirection={flexDirection}
          />
          <JustifyFlexEnd
            toggleValues={toggleValues}
            justifyContent={justifyContent}
            flexDirection={flexDirection}
          />
          <JustifySpaceBetween
            toggleValues={toggleValues}
            justifyContent={justifyContent}
            flexDirection={flexDirection}
          />
          <JustifySpaceAround
            toggleValues={toggleValues}
            justifyContent={justifyContent}
            flexDirection={flexDirection}
          />
          <JustifySpaceEvenly
            toggleValues={toggleValues}
            justifyContent={justifyContent}
            flexDirection={flexDirection}
          />
        </ValueBox>
        <ValueBox
          id="align-box"
          data-cy="toolbox-flex-align-valuebox"
          label={flexDirection === 'row' ? 'Vertical' : 'Horizontal'}
          isInGroup={{ isLast: true }}
        >
          <AlignStretch
            toggleValues={toggleValues}
            flexDirection={flexDirection}
            alignItems={alignItems || 'stretch'}
          />
          <AlignFlexStart
            toggleValues={toggleValues}
            flexDirection={flexDirection}
            alignItems={alignItems}
          />
          <AlignCenter
            toggleValues={toggleValues}
            flexDirection={flexDirection}
            alignItems={alignItems}
          />
          <AlignFlexEnd
            toggleValues={toggleValues}
            flexDirection={flexDirection}
            alignItems={alignItems}
          />
          <AlignBaseline
            toggleValues={toggleValues}
            flexDirection={flexDirection}
            alignItems={alignItems}
          />
        </ValueBox>
      </ValueGroup>
      {/* Gaps */}
      <ValueGroup>
        <Gap
          nodeIds={nodeIds}
          property="columnGap"
          label="Column gap"
          isInGroup={{ isFirst: true }}
          cssSelectorId={cssSelectorId}
        />
        <Gap
          nodeIds={nodeIds}
          isInGroup={{ isLast: true }}
          label="Row gap"
          property="rowGap"
          cssSelectorId={cssSelectorId}
        />
      </ValueGroup>
    </>
  );
};
